import { Box, Button, Paper } from "@mui/material";
import styles from "./LastPublications.module.scss";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DataTable from "../../components/Table";
import { GridColDef } from "@mui/x-data-grid";
import StageComponent from "./StageComponent";
import workflowService from "../../services/workflow.service";
import { LastPublication } from "../../models/workflow.model";
import { getLabel } from "../../misc/utils.functions";
import moment from "moment";
import AvatarList from "../../components/AvatarList";
import { useSessionStorageWithObject } from "../../hooks/useSessionStorage";
import { PublicationAdvancedFilter } from "../../misc/pagedFilter";

interface Props {
  status: string;
}

const LastPublications: React.FC<Props> = ({ status }) => {
  const { t } = useTranslation();
  const [publications, setPublications] = useState<LastPublication[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const title = t(`lastPublication.${status}`);
  const isInProgressPublications = status === "inProgress";

  const publishedColumns: GridColDef[] = [
    {
      field: "title",
      headerName: getLabel("title"),
      sortable: false,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "topic",
      headerName: getLabel("topic"),
      sortable: false,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "authors",
      headerName: getLabel("authorsCoAuthors"),
      sortable: false,
      width: 180,
      renderCell: (params) => {
        return <AvatarList users={params.row.authors} maxAvatars={3} />;
      },
    },
    {
      field: "publishedDate",
      headerName: getLabel("date"),
      sortable: false,
      width: 100,
      valueFormatter: (params) => moment(params?.value).format("MM/DD/YYYY"),
    },
  ];

  const inProgressColumns: GridColDef[] = [
    {
      field: "title",
      headerName: getLabel("title"),
      sortable: false,
      flex: 1,
      cellClassName: "title",
      minWidth: 150,
    },
    {
      field: "topic",
      headerName: getLabel("topic"),
      sortable: false,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "authors",
      headerName: getLabel("authorsCoAuthors"),
      sortable: false,
      width: 180,
      renderCell: (params) => {
        return <AvatarList users={params.row.authors} maxAvatars={3} />;
      },
    },
    {
      field: "startedDate",
      headerName: getLabel("startedDate"),
      sortable: false,
      width: 100,
      valueFormatter: (params) => moment(params?.value).format("MM/DD/YYYY"),
    },
    {
      field: "stage",
      headerName: getLabel("stage"),
      sortable: false,
      width: 120,
      renderCell: (params) => {
        return (
          <StageComponent
            workflowType={params.row.workflowType}
            stageCode={params.row.stageCode}
            stageStep={params.row.stageStep}
            isCompleted={params.row.isCompleted}
            waitingApproval={params.row.waitingApproval}
          />
        );
      },
    },
  ];

  useEffect(() => {
    setLoading(true);
    workflowService.getLastPublications(status).then((publications) => {
      setPublications(publications);
      setLoading(false);
    });
  }, [status]);

  const handleRowClick = (row: LastPublication) => {
    if (isInProgressPublications) {
      navigate(`/summary/${row.ticketNumber}`);
    } else {
      navigate(`/publications/${row.ticketNumber}`);
    }
  };

  const initFilter: PublicationAdvancedFilter = {
    pageNumber: 1,
    pageSize: 10,
    sortField: "title",
    sortDescending: false,
    search: "",
    criteria: 0,
    status: "",
    topics: [],
    filterBy: "byDivision",
    divisions: [],
    departments: [],
    countries: [],
    languages: [],
    fromDate: null,
    toDate: null,
  };

  const [currentFilter, setCurrentFilter] =
    useSessionStorageWithObject<PublicationAdvancedFilter>(
      `search_filters`,
      initFilter
    );

  const handleViewAllClick = () => {
    if (isInProgressPublications) {
      setCurrentFilter({
        ...initFilter,
        status: "InProgress",
        sortField: "startedDate",
        sortDescending: true,
      });
      navigate("/publicationsInProgress");
    } else {
      setCurrentFilter({
        ...initFilter,
        status: "Published",
        sortField: "publishedDate",
        sortDescending: true,
      });
      navigate("/publications");
    }
  };

  const getColumns = (): GridColDef[] => {
    if (isInProgressPublications) {
      return inProgressColumns;
    } else {
      return publishedColumns;
    }
  };

  return (
    <Box className={styles.main}>
      <Paper elevation={3} className={styles.basic}>
        <div className={styles.header}>
          <h3>{title}</h3>
          <div className={styles.viewAllLink} onClick={handleViewAllClick}>
            {t("viewAll")}
          </div>
        </div>

        <DataTable<LastPublication>
          rows={publications}
          columns={getColumns()}
          handleRowClick={handleRowClick}
          emptyMessage={t("noRowsMessage", {
            entity: t("publications"),
          })}
          loading={loading}
        />
      </Paper>
    </Box>
  );
};

export default LastPublications;
